<template>
  <div class="price-sheet-page" :class="{ 'screenshot-mode': screenshotMode }">
    <section class="page-card">
      <div class="page-header">
        <div>
          <h1>Loan Price Sheet</h1>
          <p>
            Build a rate sheet by loan number using Polly reprice, latest changeset,
            and pricing scenarios for 15, 30, 45, 60, 75, and 90 days.
          </p>
        </div>

        <div class="header-actions">
          <button class="secondary-button"
                  type="button"
                  :disabled="!priceSheet"
                  @click="screenshotMode = !screenshotMode">
            {{ screenshotMode ? 'Normal View' : 'Screenshot Mode' }}
          </button>

          <button class="secondary-button"
                  type="button"
                  :disabled="!visibleRows.length"
                  @click="copyTable">
            Copy Table
          </button>

          <button class="secondary-button"
                  type="button"
                  :disabled="!visibleRows.length"
                  @click="exportCsv">
            Export CSV
          </button>
        </div>
      </div>

      <div class="input-row">
        <label class="field">
          <span>Loan Number</span>
          <input v-model.trim="loanId"
                 type="text"
                 placeholder="Enter loan number"
                 :disabled="isLoading"
                 @keyup.enter="buildPriceSheet" />
        </label>

        <button class="primary-button"
                type="button"
                :disabled="isLoading || !loanId"
                @click="buildPriceSheet">
          {{ isLoading ? 'Building...' : 'Build Price Sheet' }}
        </button>
      </div>

      <div v-if="errorMessage" class="alert alert-error">
        <div class="alert-title">
          {{ errorMessage }}
        </div>

        <ul v-if="errorDetails && errorDetails.length" class="error-list">
          <li v-for="detail in errorDetails" :key="detail">
            {{ detail }}
          </li>
        </ul>

        <details v-if="errorDebugText" class="error-debug">
          <summary>Technical details</summary>
          <pre>{{ errorDebugText }}</pre>
        </details>
      </div>

      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>

      <div v-if="isLoading" class="status-card">
        <div class="spinner"></div>
        <div>
          <strong>Building price sheet</strong>
          <span>
            Running Polly reprice-pricing, loading the latest changeset, then
            pricing scenarios for each lock period.
          </span>
        </div>
      </div>
    </section>

    <section v-if="priceSheet" class="page-card summary-card">
      <div class="loan-summary">
        <div>
          <span class="summary-label">Loan Number</span>
          <strong>{{ priceSheet.loanNumber || '-' }}</strong>
        </div>

        <div>
          <span class="summary-label">Borrower</span>
          <strong>{{ priceSheet.borrowerName || '-' }}</strong>
        </div>

        <div>
          <span class="summary-label">Generated</span>
          <strong>{{ formatDateTime(priceSheet.generatedAtUtc) }}</strong>
        </div>

        <div>
          <span class="summary-label">Rows</span>
          <strong>{{ visibleRows.length }}</strong>
        </div>
      </div>

      <p class="concession-note">Prices shown may reflect existing branch or corporate concessions.</p>

      <div class="summary-lower-row">
        <div class="filters">
          <label class="field compact">
            <span>Product</span>
            <select v-model="selectedProduct">
              <option value="">All Products</option>
              <option v-for="product in availableProducts"
                      :key="product"
                      :value="product">
                {{ product }}
              </option>
            </select>
          </label>

          <label class="field compact">
            <span>Lock Period</span>
            <select v-model="selectedLockPeriod">
              <option value="">All</option>
              <option v-for="lockPeriod in availableLockPeriods"
                      :key="lockPeriod"
                      :value="String(lockPeriod)">
                {{ lockPeriod }}
              </option>
            </select>
          </label>

          <label class="field compact">
            <span>Action</span>
            <select v-model="selectedAction">
              <option value="Float Down">Float Down</option>
            </select>
          </label>
        </div>

        <div class="summary-selection-panel">
          <div v-if="selectedRow" class="summary-selected-option">
            <span class="summary-label">Selected Option</span>
            <strong>
              {{ selectedRow.productName }} —
              {{ formatRate(selectedRow.rate) }} /
              {{ formatPrice(displayPrice(selectedRow)) }} /
              {{ selectedRow.lockPeriod }} days
            </strong>

            <div v-if="submitMessage" class="submit-message">
              <i class="fa-duotone fa-circle-check"></i>
              <span>{{ submitMessage }}</span>
            </div>
          </div>

          <div v-else class="summary-selected-placeholder">
            Select a rate to submit a {{ selectedAction }}.
          </div>

          <button class="primary-button"
                  type="button"
                  :disabled="isSubmittingSelection || !selectedRow"
                  @click="submitSelectedRate">
            {{ isSubmittingSelection ? 'Submitting...' : `Submit ${selectedAction}` }}
          </button>
        </div>
      </div>
    </section>

    <section v-if="priceSheet" class="rate-sheet-card">
      <div v-for="group in groupedVisibleRows"
           :key="group.groupKey"
           class="product-group">
        <button class="product-header"
                type="button"
                @click.prevent.stop="toggleGroup(group.groupKey)">
          <div class="product-title">
            <span class="collapse-icon">
              {{ isGroupCollapsed(group.groupKey) ? '+' : '−' }}
            </span>

            {{ group.productName }}
          </div>

          <div class="product-meta">
            <span>{{ group.rows.length }} rows</span>
            <span v-if="group.productCode">Code: {{ group.productCode }}</span>
            <span v-if="selectedRow && selectedRow.productName === group.productName">
              Selected: {{ formatRate(selectedRow.rate) }} / {{ formatPrice(displayPrice(selectedRow)) }}
            </span>
          </div>
        </button>

        <div v-show="!isGroupCollapsed(group.groupKey)" class="table-wrap">
          <table class="rate-table">
            <thead>
              <tr>
                <th class="select-col">Select</th>
                <th>Rate</th>
                <th>Original</th>
                <th>Float Down</th>
                <th>Price Change</th>
                <th>P&amp;I</th>
                <th>Credit / Cost</th>
                <th>Lock Period</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in group.rows"
                  :key="rowKey(row)"
                  :class="{ selected: isSelected(row) }"
                  @click="selectRow(row)">
                <td class="select-col">
                  <input type="checkbox"
                         :checked="isSelected(row)"
                         @change="selectRow(row)"
                         @click.stop />
                </td>

                <td>{{ formatRate(row.rate) }}</td>
                <td class="col-original">{{ formatPrice(originalPrice(row)) }}</td>
                <td>{{ formatPrice(displayPrice(row)) }}</td>
                <td :class="fdChangeClass(row)">{{ formatFdChange(row) }}</td>
                <td>{{ formatCurrency(row.principalAndInterest) }}</td>
                <td>{{ formatCreditCost(row) }}</td>
                <td>{{ row.lockPeriod }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="!visibleRows.length" class="empty-state">
        No price sheet rows match the selected filters.
      </div>
    </section>

  </div>
</template>

<script>
  import api from '@/api/api.service'
  import auth from '@/authConfig.js'

  export default {
    name: 'LoanPriceSheet',

    data() {
      return {
        loanId: '',
        isLoading: false,
        errorMessage: '',
        errorDetails: [],
        errorDebugText: '',
        successMessage: '',
        priceSheet: null,
        selectedProduct: '',
        selectedLockPeriod: '',
        selectedAction: 'Float Down',
        selectedRow: null,
        screenshotMode: false,
        collapsedGroups: {},
        isSubmittingSelection: false,
        submitMessage: '',
      };
    },

    computed: {
      availableProducts() {
        const names = new Set();

        const rows = this.priceSheet && this.priceSheet.rows
          ? this.priceSheet.rows
          : [];

        rows.forEach(row => {
          if (row.productName) names.add(String(row.productName));
        });

        return Array.from(names)
          .map(x => String(x))
          .sort((a, b) => a.localeCompare(b));
      },

      availableLockPeriods() {
        const values = new Set();

        const rows = this.priceSheet && this.priceSheet.rows
          ? this.priceSheet.rows
          : [];

        rows.forEach(row => {
          if (row.lockPeriod) values.add(Number(row.lockPeriod));
        });

        return Array.from(values)
          .map(x => Number(x))
          .sort((a, b) => a - b);
      },

      visibleRows() {
        let rows = this.priceSheet && this.priceSheet.rows
          ? [...this.priceSheet.rows]
          : [];

        if (this.selectedProduct) {
          rows = rows.filter(row => row.productName === this.selectedProduct);
        }

        if (this.selectedLockPeriod) {
          const lockPeriod = Number(this.selectedLockPeriod);
          rows = rows.filter(row => row.lockPeriod === lockPeriod);
        }

        return rows.sort((a, b) => {
          const productA = String(a.productName || '');
          const productB = String(b.productName || '');

          if (productA !== productB) {
            return productA.localeCompare(productB);
          }

          if (a.lockPeriod !== b.lockPeriod) {
            return a.lockPeriod - b.lockPeriod;
          }

          return a.rate - b.rate;
        });
      },

      groupedVisibleRows() {
        const map = new Map();

        this.visibleRows.forEach(row => {
          const key = `${row.productName || 'Unknown Product'}|${row.productCode || ''}`;

          if (!map.has(key)) {
            map.set(key, {
              groupKey: key,
              productName: row.productName || 'Unknown Product',
              productCode: row.productCode,
              rows: []
            });
          }

          map.get(key).rows.push(row);
        });

        return Array.from(map.values());
      }
    },

    mounted() {
      const routeLoanId = this.$route && this.$route.params
        ? this.$route.params.loanId
        : '';

      if (typeof routeLoanId === 'string' && routeLoanId.trim()) {
        this.loanId = routeLoanId.trim();
      }
    },

    methods: {
      floatDownPrice(row) {
        if (!row || row.price === null || row.price === undefined || Number.isNaN(Number(row.price))) {
          return null
        }

        return Number((Number(row.price) - 0.5).toFixed(3))
      },

      displayPrice(row) {
        if (this.selectedAction === 'Float Down') {
          return this.floatDownPrice(row)
        }

        return row.price
      },

      async submitSelectedRate() {
        this.errorMessage = ''
        this.errorDetails = []
        this.errorDebugText = ''
        this.successMessage = ''
        this.submitMessage = ''

        if (!this.priceSheet) {
          this.errorMessage = 'Build a price sheet before submitting a selection.'
          return
        }

        if (!this.selectedRow) {
          this.errorMessage = 'Select a rate before submitting.'
          return
        }

        if (this.selectedAction !== 'Float Down') {
          this.errorMessage = 'Only Float Down is currently supported.'
          return
        }

        const peRequestId =
          this.selectedRow.peRequestId ||
          this.selectedRow.PeRequestId ||
          this.selectedRow.pe_request_id ||
          this.selectedRow.perequestid ||
          ''

        if (!peRequestId) {
          this.errorMessage = 'Missing Polly peRequestId for the selected rate.'
          this.errorDetails = [
            'Refresh and rebuild the price sheet, then select the rate again.',
            'If this keeps happening, confirm the API response includes peRequestId on each row.'
          ]
          return
        }

        this.isSubmittingSelection = true

        try {
          const tokenResponse = await auth.acquireTokenResponse()

          const request = {
            loanId: this.priceSheet.loanId || this.loanId || '',
            loanNumber: this.priceSheet.loanNumber || '',
            action: this.selectedAction,

            customFieldId: 'CX.POLLY.FLOATDOWN',
            customFieldValue: 'FloatDown',

            peRequestId: peRequestId,

            selectedRate: Number(this.selectedRow.rate),
            selectedPrice: Number(this.displayPrice(this.selectedRow)),
            lockPeriod: Number(this.selectedRow.lockPeriod),

            productName: this.selectedRow.productName || '',
            productCode: this.selectedRow.productCode || '',
            investorName: this.selectedRow.investorName || ''
          }

          const response = await api.SubmitLoanPriceSheetSelection(
            request,
            tokenResponse.accessToken
          )

          const wrapper = response?.data || {}
          const result = wrapper?.data || wrapper || {}

          const isSuccess =
            wrapper.issuccess !== undefined
              ? wrapper.issuccess
              : wrapper.isSuccess !== undefined
                ? wrapper.isSuccess
                : wrapper.IsSuccess !== undefined
                  ? wrapper.IsSuccess
                  : result.issuccess !== undefined
                    ? result.issuccess
                    : result.isSuccess !== undefined
                      ? result.isSuccess
                      : result.IsSuccess !== undefined
                        ? result.IsSuccess
                        : result.success === true

          const message =
            wrapper.message ||
            wrapper.Message ||
            result.message ||
            ''

          if (!isSuccess) {
            throw new Error(message || result.error || 'Unable to submit the selected rate.')
          }

          const selectedRateText = this.formatRate(this.selectedRow.rate)
          const selectedPriceText = this.formatPrice(this.displayPrice(this.selectedRow))
          const selectedLockPeriodText = `${Number(this.selectedRow.lockPeriod)} days`
          const selectedProductText = this.selectedRow.productName || 'Selected product'
          const selectedLoanNumberText = this.priceSheet.loanNumber || request.loanNumber || request.loanId

          this.successMessage =
            message ||
            `Float Down submitted to Polly for loan ${selectedLoanNumberText}. ${selectedProductText} at ${selectedRateText} rate / ${selectedPriceText} price / ${selectedLockPeriodText}. CX.POLLY.FLOATDOWN was saved as FloatDown.`

          this.submitMessage =
            `Submitted to Polly. ${selectedRateText} rate / ${selectedPriceText} price / ${selectedLockPeriodText}. CX.POLLY.FLOATDOWN = FloatDown.`
        } catch (error) {
          const friendlyError = error && error.isFriendlyError
            ? error
            : this.formatFriendlyError(error?.response?.data || error)

          this.errorMessage = friendlyError.message || 'Unable to submit the selected rate.'
          this.errorDetails = friendlyError.details || []
          this.errorDebugText = friendlyError.debugText || ''
        } finally {
          this.isSubmittingSelection = false
        }
      },

      toggleGroup(groupKey) {
        this.collapsedGroups = {
          ...this.collapsedGroups,
          [groupKey]: !this.collapsedGroups[groupKey]
        };
      },

      isGroupCollapsed(groupKey) {
        return this.collapsedGroups[groupKey] === true;
      },

      async buildPriceSheet() {
        this.errorMessage = '';
        this.errorDetails = [];
        this.errorDebugText = '';
        this.successMessage = '';
        this.selectedRow = null;
        this.screenshotMode = false;
        this.collapsedGroups = {};
        this.submitMessage = '';

        if (!this.loanId) {
          this.errorMessage = 'Loan ID is required.';
          return;
        }

        this.isLoading = true;

        try {
          const apiBaseUrl = (process.env.VUE_APP_API_ENDPOINT || '').replace(/\/$/, '');
          const url = `${apiBaseUrl}/api/SwiftAppAI/GetLoanPriceSheet`;

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ loanId: this.loanId })
          });

          const responseText = await response.text();

          if (!response.ok) {
            const friendlyError = this.formatFriendlyError(responseText, response.status);
            throw friendlyError;
          }

          const wrapped = JSON.parse(responseText);

          const isSuccess =
            wrapped.issuccess !== undefined
              ? wrapped.issuccess
              : wrapped.isSuccess !== undefined
                ? wrapped.isSuccess
                : wrapped.IsSuccess;

          const message =
            wrapped.message ||
            wrapped.Message ||
            '';

          const payload =
            wrapped.data ||
            wrapped.Data;

          if (!isSuccess || !payload) {
            throw this.formatFriendlyError(wrapped || message);
          }

          this.priceSheet = this.normalizePriceSheetResponse(payload);

          this.successMessage = `Price sheet built with ${this.priceSheet.rows.length} rows.`;
        } catch (error) {
          this.priceSheet = null;

          const friendlyError = error && error.isFriendlyError
            ? error
            : this.formatFriendlyError(error);

          this.errorMessage = friendlyError.message;
          this.errorDetails = friendlyError.details || [];
          this.errorDebugText = friendlyError.debugText || '';
        } finally {
          this.isLoading = false;
        }
      },

      formatFriendlyError(error, httpStatus) {
        const normalized = this.normalizeErrorPayload(error);
        const rawText = normalized.rawText || '';
        const messages = normalized.messages || [];
        const details = [];

        const combined = [
          rawText,
          ...messages
        ].join(' ');

        let userMessage = 'Unable to complete the request.';

        if (httpStatus === 404 || /notfound|not found/i.test(combined)) {
          userMessage = 'Polly could not find the information needed to build this price sheet.';
        }

        if (/no prior successful lock/i.test(combined)) {
          userMessage = 'Polly cannot build this price sheet because the loan does not have a prior successful lock.';

          details.push(
            'Confirm the loan number or loan GUID is correct.',
            'Confirm the loan has already been successfully locked in Polly.',
            'After the loan has a successful lock, try building the price sheet again.'
          );
        } else if (/loanid|loan id/i.test(combined)) {
          details.push('Confirm the loan ID is correct and try again.');
        }

        if (!details.length && messages.length) {
          messages
            .map(x => this.cleanErrorLine(x))
            .filter(Boolean)
            .filter(x => !this.isTechnicalErrorLine(x))
            .slice(0, 5)
            .forEach(x => details.push(x));
        }

        if (!details.length) {
          details.push('Please try again. If the issue continues, send the technical details to support.');
        }

        return {
          isFriendlyError: true,
          message: userMessage,
          details,
          debugText: this.buildDebugErrorText(normalized, httpStatus)
        };
      },

      normalizeErrorPayload(error) {
        const result = {
          rawText: '',
          messages: []
        };

        if (!error) {
          return result;
        }

        if (typeof error === 'string') {
          result.rawText = error;
          this.collectErrorMessages(error, result.messages);
          return result;
        }

        if (error instanceof Error) {
          result.rawText = error.message || '';
          this.collectErrorMessages(error.message || '', result.messages);

          if (error.stack) {
            result.messages.push(error.stack);
          }

          return result;
        }

        if (typeof error === 'object') {
          result.rawText = JSON.stringify(error, null, 2);
          this.collectErrorMessages(error, result.messages);
          return result;
        }

        result.rawText = String(error);
        this.collectErrorMessages(result.rawText, result.messages);

        return result;
      },

      collectErrorMessages(value, messages) {
        if (value === null || value === undefined) {
          return;
        }

        if (typeof value === 'string') {
          const trimmed = value.trim();

          if (!trimmed) {
            return;
          }

          messages.push(trimmed);

          const parsed = this.tryParseJson(trimmed);
          if (parsed) {
            this.collectErrorMessages(parsed, messages);
          }

          return;
        }

        if (Array.isArray(value)) {
          value.forEach(item => this.collectErrorMessages(item, messages));
          return;
        }

        if (typeof value === 'object') {
          const keysToCheck = [
            'message',
            'Message',
            'error',
            'Error',
            'detail',
            'Detail',
            'details',
            'Details',
            'errors',
            'Errors',
            'title',
            'Title'
          ];

          keysToCheck.forEach(key => {
            if (value[key] !== undefined) {
              this.collectErrorMessages(value[key], messages);
            }
          });
        }
      },

      tryParseJson(value) {
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      },

      cleanErrorLine(value) {
        if (!value) {
          return '';
        }

        return String(value)
          .replace(/\\r\\n/g, ' ')
          .replace(/\\n/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      },

      isTechnicalErrorLine(value) {
        const text = String(value || '');

        return (
          /System\./i.test(text) ||
          / at .*\.cs:line /i.test(text) ||
          /stack trace/i.test(text) ||
          /CancellationToken/i.test(text) ||
          /D:\\/i.test(text)
        );
      },

      buildDebugErrorText(normalized, httpStatus) {
        const lines = [];

        if (httpStatus) {
          lines.push(`HTTP Status: ${httpStatus}`);
        }

        if (normalized.rawText) {
          lines.push(normalized.rawText);
        }

        return lines.join('\n\n').trim();
      },

      normalizePriceSheetResponse(payload) {
        const rawRows =
          payload.rows ||
          payload.Rows ||
          []

        return {
          loanId:
            payload.loanid ||
            payload.loanId ||
            payload.LoanId ||
            '',

          loanNumber:
            payload.loannumber ||
            payload.loanNumber ||
            payload.LoanNumber ||
            '',

          borrowerName:
            payload.borrowername ||
            payload.borrowerName ||
            payload.BorrowerName ||
            '',

          generatedAtUtc:
            payload.generatedatutc ||
            payload.generatedAtUtc ||
            payload.GeneratedAtUtc ||
            '',

          currentLockRate:
            payload.currentlockrate != null ? Number(payload.currentlockrate)
            : payload.currentLockRate != null ? Number(payload.currentLockRate)
            : payload.OriginalLockRate != null ? Number(payload.OriginalLockRate)
            : null,

          currentLockPrice:
            payload.currentlockprice != null ? Number(payload.currentlockprice)
            : payload.currentLockPrice != null ? Number(payload.currentLockPrice)
            : payload.OriginalLockPrice != null ? Number(payload.OriginalLockPrice)
            : null,

          rows: rawRows.map(row => ({
            peRequestId:
              row.perequestid ||
              row.peRequestId ||
              row.PeRequestId ||
              row.pe_request_id ||
              '',

            productName:
              row.productname ||
              row.productName ||
              row.ProductName ||
              '',

            productCode:
              row.productcode ||
              row.productCode ||
              row.ProductCode ||
              '',

            investorName:
              row.investorname ||
              row.investorName ||
              row.InvestorName ||
              '',

            rate: Number(
              row.rate !== undefined
                ? row.rate
                : row.Rate
            ),

            price: Number(
              row.price !== undefined
                ? row.price
                : row.Price
            ),

            principalAndInterest:
              row.principalandinterest !== undefined
                ? row.principalandinterest
                : row.principalAndInterest !== undefined
                  ? row.principalAndInterest
                  : row.PrincipalAndInterest,

            creditCostPercent:
              row.creditcostpercent !== undefined
                ? row.creditcostpercent
                : row.creditCostPercent !== undefined
                  ? row.creditCostPercent
                  : row.CreditCostPercent,

            creditCostAmount:
              row.creditcostamount !== undefined
                ? row.creditcostamount
                : row.creditCostAmount !== undefined
                  ? row.creditCostAmount
                  : row.CreditCostAmount,

            lockPeriod: Number(
              row.lockperiod !== undefined
                ? row.lockperiod
                : row.lockPeriod !== undefined
                  ? row.lockPeriod
                  : row.LockPeriod
            ),

            isOriginalRate:
              row.iscurrentrate !== undefined
                ? row.iscurrentrate
                : row.isOriginalRate !== undefined
                  ? row.isOriginalRate
                  : row.IsOriginalRate
          }))
        }
      },

      selectRow(row) {
        this.selectedRow = row;
        this.submitMessage = '';
      },

      isSelected(row) {
        if (!this.selectedRow) return false;
        return this.rowKey(this.selectedRow) === this.rowKey(row);
      },

      rowKey(row) {
        return [
          row.peRequestId || '',
          row.productCode || row.productName,
          row.investorName || '',
          row.rate,
          row.price,
          row.lockPeriod
        ].join('|')
      },

      copyTable() {
        const text = this.buildTabDelimitedText(this.visibleRows);

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text);
          this.successMessage = 'Copied price sheet rows to clipboard.';
          return;
        }

        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);

        this.successMessage = 'Copied price sheet rows to clipboard.';
      },

      exportCsv() {
        const csv = this.buildCsvText(this.visibleRows);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `loan-price-sheet-${this.loanId || 'loan'}.csv`;
        link.click();

        URL.revokeObjectURL(url);
      },

      buildTabDelimitedText(rows) {
        const headers = [
          'Product',
          'Product Code',
          'Investor',
          'Rate',
          'Original',
          'Float Down',
          'Price Change',
          'P&I',
          'Credit / Cost',
          'Lock Period'
        ];

        const body = rows.map(row => [
          row.productName,
          row.productCode || '',
          row.investorName || '',
          this.formatRate(row.rate),
          this.formatPrice(this.originalPrice(row)),
          this.formatPrice(this.displayPrice(row)),
          this.formatFdChange(row),
          this.formatCurrency(row.principalAndInterest),
          this.formatCreditCost(row),
          String(row.lockPeriod)
        ]);

        return [headers, ...body].map(cols => cols.join('\t')).join('\n');
      },

      buildCsvText(rows) {
        const headers = [
          'Product',
          'Product Code',
          'Investor',
          'Rate',
          'Original',
          'Float Down',
          'Price Change',
          'P&I',
          'Credit / Cost',
          'Lock Period'
        ];

        const body = rows.map(row => [
          row.productName,
          row.productCode || '',
          row.investorName || '',
          this.formatRate(row.rate),
          this.formatPrice(this.originalPrice(row)),
          this.formatPrice(this.displayPrice(row)),
          this.formatFdChange(row),
          this.formatCurrency(row.principalAndInterest),
          this.formatCreditCost(row),
          String(row.lockPeriod)
        ]);

        return [headers, ...body]
          .map(cols => cols.map(this.escapeCsv).join(','))
          .join('\n');
      },

      escapeCsv(value) {
        const safe = value == null ? '' : String(value);

        if (safe.includes(',') || safe.includes('"') || safe.includes('\n')) {
          return `"${safe.replace(/"/g, '""')}"`;
        }

        return safe;
      },

      formatRate(value) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
        return Number(value).toFixed(3);
      },

      formatPrice(value) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
        return Number(value).toFixed(3);
      },

      formatCurrency(value) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';

        return Number(value).toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0
        });
      },

      formatCreditCost(row) {
        const percent =
          row.creditCostPercent === null || row.creditCostPercent === undefined
            ? null
            : Number(row.creditCostPercent);

        const amount =
          row.creditCostAmount === null || row.creditCostAmount === undefined
            ? null
            : Number(row.creditCostAmount);

        if (percent === null && amount === null) return '-';

        const percentText =
          percent === null || Number.isNaN(percent)
            ? '-'
            : Math.abs(percent).toFixed(3);

        const amountText =
          amount === null || Number.isNaN(amount)
            ? ''
            : ` (${this.formatCurrency(Math.abs(amount))})`;

        return `${percentText}${amountText}`;
      },

      originalPrice(row) {
        return this.priceSheet && this.priceSheet.currentLockPrice != null
          ? this.priceSheet.currentLockPrice
          : row.price
      },

      formatDelta(value) {
        if (value === null || value === undefined || Number.isNaN(value)) return '-'
        const d = Number(value.toFixed(3))
        if (d === 0) return '—'
        return (d > 0 ? '+' : '') + d.toFixed(3)
      },

      deltaClass(delta) {
        if (delta > 0) return 'price-change-positive'
        if (delta < 0) return 'price-change-negative'
        return ''
      },

      formatPriceChange(row) {
        return this.formatDelta(Number(row.price) - Number(this.originalPrice(row)))
      },

      priceChangeClass(row) {
        return this.deltaClass(Number(row.price) - Number(this.originalPrice(row)))
      },

      formatFdChange(row) {
        const fdPrice = this.displayPrice(row)
        if (fdPrice === null || fdPrice === undefined) return '-'
        return this.formatDelta(Number(fdPrice) - Number(this.originalPrice(row)))
      },

      fdChangeClass(row) {
        const fdPrice = this.displayPrice(row)
        if (fdPrice === null || fdPrice === undefined) return ''
        return this.deltaClass(Number(fdPrice) - Number(this.originalPrice(row)))
      },

      formatDateTime(value) {
        if (!value) return '-';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return date.toLocaleString();
      }
    }
  };
</script>

<style scoped>
  .col-original {
    color: var(--text-muted, #667085);
  }

  .price-change-positive {
    color: #027a48;
    font-weight: 600;
  }

  .price-change-negative {
    color: #b42318;
    font-weight: 600;
  }

  .concession-note {
    margin: 0 0 4px;
    font-size: 11px;
    color: var(--text-muted, #667085);
    font-style: italic;
  }

  .summary-card {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .summary-lower-row {
    display: grid;
    grid-template-columns: minmax(520px, auto) minmax(420px, 1fr);
    gap: 18px;
    align-items: end;
  }

  .summary-selection-panel {
    display: flex;
    align-items: end;
    justify-content: flex-end;
    gap: 14px;
    min-width: 0;
  }

  .summary-selected-option {
    min-width: 0;
    text-align: right;
  }

    .summary-selected-option strong {
      display: block;
      margin-top: 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 760px;
    }

  .summary-selected-placeholder {
    color: var(--text-muted, #667085);
    font-size: 13px;
    font-weight: 700;
  }

  .submit-message {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 4px;
    font-size: 13px;
    font-weight: 700;
    color: #05603a;
  }

    .submit-message i {
      font-size: 13px;
    }

  .alert-title {
    font-weight: 800;
  }

  .error-list {
    margin: 8px 0 0;
    padding-left: 20px;
  }

    .error-list li {
      margin: 4px 0;
    }

  .error-debug {
    margin-top: 10px;
  }

    .error-debug summary {
      cursor: pointer;
      font-weight: 700;
    }

    .error-debug pre {
      margin: 8px 0 0;
      padding: 10px;
      max-height: 220px;
      overflow: auto;
      white-space: pre-wrap;
      word-break: break-word;
      background: rgba(127, 29, 29, 0.08);
      border-radius: 6px;
      font-size: 12px;
    }

  .price-sheet-page {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 18px;
    color: var(--text-color, #1f2937);
    max-height: calc(100vh - 112px);
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
    padding-bottom: 96px;
  }

  .rate-sheet-card {
    overflow: visible;
  }

  .page-card,
  .rate-sheet-card {
    background: var(--surface-card, #ffffff);
    border: 1px solid var(--surface-border, #d9dee7);
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  }

  .page-card {
    padding: 18px;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    gap: 18px;
    align-items: flex-start;
    margin-bottom: 18px;
  }

    .page-header h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
    }

    .page-header p {
      margin: 6px 0 0;
      color: var(--text-muted, #667085);
      font-size: 14px;
    }

  .header-actions,
  .input-row,
  .filters,
  .selected-card {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
  }

  .input-row {
    margin-top: 8px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 280px;
    flex: 1;
  }

    .field.compact {
      min-width: 180px;
      flex: 0 0 auto;
    }

    .field span,
    .summary-label {
      font-size: 12px;
      font-weight: 700;
      color: var(--text-muted, #667085);
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

  input,
  select {
    height: 38px;
    border: 1px solid var(--surface-border, #cfd6e4);
    border-radius: 6px;
    padding: 0 10px;
    color: var(--text-color, #1f2937);
    background: var(--input-bg, #ffffff);
    outline: none;
  }

    input:focus,
    select:focus {
      border-color: var(--primary-color, #174a7c);
      box-shadow: 0 0 0 3px rgba(23, 74, 124, 0.13);
    }

  .primary-button,
  .secondary-button {
    height: 38px;
    border-radius: 6px;
    padding: 0 14px;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid transparent;
  }

  .primary-button {
    background: var(--primary-color, #174a7c);
    color: #ffffff;
  }

  .secondary-button {
    background: var(--surface-card, #ffffff);
    color: var(--primary-color, #174a7c);
    border-color: var(--primary-color, #174a7c);
  }

    .primary-button:disabled,
    .secondary-button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

  .alert {
    margin-top: 14px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 14px;
  }

  .alert-success {
    background: #ecfdf3;
    color: #05603a;
    border: 1px solid #abefc6;
  }

  .alert-error {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }

  .status-card {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
    padding: 12px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

    .status-card span {
      display: block;
      margin-top: 2px;
      color: var(--text-muted, #667085);
      font-size: 13px;
    }

  .spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #dbe4ef;
    border-top-color: var(--primary-color, #174a7c);
    border-radius: 50%;
    animation: spin 0.85s linear infinite;
  }

  .loan-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(140px, 1fr));
    gap: 14px;
  }

    .loan-summary strong {
      display: block;
      margin-top: 4px;
    }

  .filters {
    margin-top: 0;
  }

  .product-group {
    border-bottom: 1px solid var(--surface-border, #d9dee7);
  }

    .product-group:last-child {
      border-bottom: 0;
    }

  .product-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 48px;
    padding: 0 18px;
    background: var(--primary-color, #174a7c);
    color: #ffffff;
    border: 0;
    cursor: pointer;
    text-align: left;
  }

    .product-header:hover {
      filter: brightness(0.96);
    }

  .collapse-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    margin-right: 6px;
    font-weight: 900;
  }

  .product-title {
    font-weight: 800;
  }

  .product-meta {
    display: flex;
    gap: 16px;
    font-size: 13px;
    opacity: 0.95;
  }

  .table-wrap {
    overflow-x: auto;
    overflow-y: visible;
  }

  .rate-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    user-select: text;
  }

    .rate-table th,
    .rate-table td {
      border-bottom: 1px solid var(--surface-border, #e5e7eb);
      padding: 7px 10px;
      text-align: center;
      white-space: nowrap;
    }

    .rate-table th {
      font-weight: 800;
      background: var(--surface-muted, #f8fafc);
      color: var(--text-color, #1f2937);
    }

    .rate-table tbody tr {
      cursor: pointer;
    }

      .rate-table tbody tr:hover {
        background: #f3f8ff;
      }

      .rate-table tbody tr.selected {
        background: #e6f0fa;
        outline: 2px solid rgba(23, 74, 124, 0.25);
        outline-offset: -2px;
      }

  .select-col {
    width: 74px;
  }

  .empty-state {
    padding: 24px;
    text-align: center;
    color: var(--text-muted, #667085);
  }

  .selected-card {
    justify-content: space-between;
    align-items: center;
  }

  .screenshot-mode {
    gap: 8px;
    padding: 8px;
    max-height: calc(100vh - 112px);
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
  }

    .screenshot-mode .page-header > div:first-child,
    .screenshot-mode .input-row,
    .screenshot-mode .alert,
    .screenshot-mode .status-card,
    .screenshot-mode .summary-selection-panel {
      display: none;
    }

    .screenshot-mode .page-header {
      justify-content: flex-end;
      margin-bottom: 0;
    }

    .screenshot-mode .page-card:first-child {
      padding: 8px;
    }

    .screenshot-mode .page-card {
      padding: 10px;
    }

    .screenshot-mode .loan-summary {
      grid-template-columns: repeat(4, minmax(100px, 1fr));
      gap: 8px;
    }

    .screenshot-mode .filters {
      display: none;
    }

    .screenshot-mode .rate-sheet-card {
      box-shadow: none;
      border-radius: 6px;
    }

    .screenshot-mode .product-header {
      min-height: 30px;
      padding: 0 8px;
    }

    .screenshot-mode .product-title {
      font-size: 12px;
    }

    .screenshot-mode .product-meta {
      font-size: 11px;
    }

    .screenshot-mode .rate-table {
      font-size: 11px;
    }

      .screenshot-mode .rate-table th,
      .screenshot-mode .rate-table td {
        padding: 3px 6px;
      }

    .screenshot-mode .select-col {
      width: 48px;
    }

  @supports not (scrollbar-gutter: stable) {
    .price-sheet-page {
      padding-right: 34px;
    }

    .screenshot-mode {
      padding-right: 24px;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 900px) {
    .page-header,
    .loan-summary {
      display: block;
    }

    .header-actions {
      margin-top: 12px;
    }

    .loan-summary > div {
      margin-bottom: 12px;
    }

    .summary-lower-row {
      display: block;
    }

    .summary-selection-panel {
      justify-content: flex-start;
      align-items: flex-start;
      margin-top: 12px;
    }

    .summary-selected-option {
      text-align: left;
    }

      .summary-selected-option strong {
        white-space: normal;
        max-width: none;
      }
  }
</style>
